import { ClientLoggingService } from './shared/client-logging.service';
import { Routes, UrlSegment, UrlMatchResult } from '@angular/router';
import { MainViewComponent } from './components/project/main-view/main-view.component';
import { UnauthorizedComponent } from './components/root/unauthorized/unauthorized.component';
import { LandingPageComponent } from './components/root/landing-page/landing-page.component';
import { NoAccessComponent } from './components/root/no-access/no-access.component';
import { ServerErrorComponent } from './components/root/server-error/server-error.component';
import { adminGuard } from './shared/admin.guard';
import { TabService } from './shared/tab.service';

/**
 * Matches: files/:fileId/pages/:pageNumber/[...regionPath]/:tab
 *
 * The last segment is always the tab (a known static tab ID like 'overview',
 * 'preview', 'compliance', 'references', or a dynamic tab like a compliance
 * section ID). Everything between pageNumber and the tab is the optional
 * region hierarchy path (e.g., drawing-area/n-front-elevation).
 *
 * Examples:
 *   files/1/pages/8/preview                          → tab=preview, no region
 *   files/1/pages/8/drawing-area/preview             → tab=preview, region=drawing-area
 *   files/1/pages/8/drawing-area/front-elev/preview  → tab=preview, region=drawing-area/front-elev
 *   files/1/pages/6/IBC2018P6_Ch11_Sec1105           → tab=IBC2018P6_Ch11_Sec1105 (dynamic), no region
 */
export function pageWithOptionalRegionMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  // Minimum: files/:fileId/pages/:pageNumber/:tab → 5 segments
  if (segments.length < 5) return null;
  if (segments[0].path !== 'files') return null;
  if (segments[2].path !== 'pages') return null;

  const lastPath = segments[segments.length - 1].path;
  const isStaticTab = TabService.STATIC_TAB_IDS.includes(lastPath);

  // For 5 segments (no region), the last segment is the tab (static or dynamic)
  // For 6+ segments, the last segment MUST be a known static tab, otherwise
  // we can't distinguish region slugs from dynamic tab IDs.
  if (segments.length > 5 && !isStaticTab) {
    return null;
  }

  const posParams: Record<string, UrlSegment> = {
    fileId: segments[1],
    pageNumber: segments[3],
    tab: segments[segments.length - 1],
  };

  if (segments.length > 5) {
    const regionSlugs = segments.slice(4, segments.length - 1);
    posParams['regionPath'] = new UrlSegment(regionSlugs.map(s => s.path).join('/'), {});
  }

  return { consumed: segments, posParams };
}

export const routes: Routes = [
  // Legal pages (no auth required)
  {
    path: 'privacy',
    loadComponent: () => import('./components/legal/legal-page.component').then(m => m.LegalPageComponent),
    data: { pageType: 'privacy' },
  },
  {
    path: 'privacy.html',
    redirectTo: 'privacy',
    pathMatch: 'full',
  },
  {
    path: 'terms',
    loadComponent: () => import('./components/legal/legal-page.component').then(m => m.LegalPageComponent),
    data: { pageType: 'terms' },
  },
  {
    path: 'terms.html',
    redirectTo: 'terms',
    pathMatch: 'full',
  },
  {
    path: 'admin',
    loadComponent: () => import('./components/admin/admin.component').then(m => m.AdminComponent),
    canActivate: [adminGuard],
  },
  {
    path: 'billing',
    loadComponent: () => {
      ClientLoggingService.log('INFO', '[ROUTES] Attempting to load billing component...');
      try {
        return import('./components/billing/billing.component').then(m => {
          ClientLoggingService.log('INFO', '[ROUTES] Billing component module loaded successfully:', !!m);
          ClientLoggingService.log('INFO', '[ROUTES] BillingComponent class found:', !!m.BillingComponent);
          return m.BillingComponent;
        });
      } catch (error) {
        ClientLoggingService.log('ERROR', '[ROUTES] Error in billing component loading:', error);
        throw error;
      }
    },
    children: [
      { path: 'overview', children: [] },
      { path: 'transactions', children: [] },
      { path: 'projects', children: [] },
      { path: '', pathMatch: 'full', redirectTo: 'overview' },
    ],
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'no-access',
    component: NoAccessComponent,
  },
  {
    path: 'error',
    component: ServerErrorComponent,
  },
  {
    path: 'signed-out',
    loadComponent: () => import('./components/root/signed-out/signed-out.component').then(m => m.SignedOutComponent),
  },
  {
    path: 'version',
    loadComponent: () => import('./components/root/version/version.component').then(m => m.VersionComponent),
  },
  {
    path: 'print-preview/chat-session/:sessionId',
    loadComponent: () => import('./components/chat/print-view/print-view.component').then(m => m.PrintViewComponent),
  },
  {
    path: 'projects/:planId',
    component: MainViewComponent,
    children: [
      {
        path: 'files/:fileId/overview',
        data: { fileTab: 'overview' },
        children: [],
      },
      {
        path: 'files/:fileId/compliance',
        data: { fileTab: 'compliance' },
        children: [],
      },
      {
        matcher: pageWithOptionalRegionMatcher,
        children: [],
      },
      {
        path: 'pages/:pageNumber/:tab',
        // Legacy route for backward compatibility - redirects to first file
        redirectTo: 'files/1/pages/:pageNumber/:tab',
      },
      {
        path: 'settings',
        // No component - parent MainViewComponent handles rendering via *ngIf
        children: [
          {
            path: 'overview',
            // No component - handled by parent's tab logic
            children: [], // Empty children array to satisfy Angular route validator
          },
          {
            path: 'files',
            // No component - handled by parent's tab logic
            children: [], // Empty children array to satisfy Angular route validator
          },
          {
            path: 'settings',
            // No component - handled by parent's tab logic
            children: [], // Empty children array to satisfy Angular route validator
          },
          {
            path: 'config',
            redirectTo: 'settings',
          },
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'overview', // Default to overview tab
          },
        ],
      },
      {
        path: 'tasks',
        children: [
          {
            path: ':taskId',
            children: [],
          },
          {
            path: '',
            children: [],
          },
        ],
      },
      {
        // The project's CAT graphs (Issue #2399), shown by the project page's tab logic like Tasks.
        path: 'cat-graphs',
        children: [
          {
            path: ':graphId',
            children: [],
          },
          {
            path: '',
            children: [],
          },
        ],
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'settings', // Default to project settings page
      },
    ],
  },
  {
    path: '',
    component: LandingPageComponent,
  },
  {
    path: '**',
    redirectTo: '/',
    pathMatch: 'full',
  },
];
